/**
 * Supplier catalog refusals (BLU-1268), plus the Order and wait ones that
 * ride the same marker (BLU-1506, `orderAndWaitErrorText` and
 * `supplierSupplyErrorText`): the browser half of
 * `apps/backend/src/lib/supplier-catalog-errors.ts`.
 *
 * The server throws a TRPCError whose MESSAGE is `SUPPLIER_CATALOG_<KEY>:<json>`
 * and whose `cause.code` is `SUPPLIER_CATALOG_<KEY>` (FORWARDABLE_CAUSE_CODES).
 * The parsing (cause first, the marker as the fallback, because tRPC 11.7.1
 * drops wrapped causes on the wire today) is the shared `parseMarkerError`;
 * this file keeps the key list and the sentences. `catalog_add_to_location` runs the
 * Base garments writers, so their `BASE_GARMENT_<KEY>` refusals reach Add too;
 * those keep their own words (`anyBaseGarmentErrorText`). Operators never see
 * a marker or an error code: every refusal maps to a plain sentence.
 */
import type { TFunction } from 'i18next';
import { SUPPLIER_CATALOG_ERROR_KEYS, type SupplierCatalogErrorKey } from '@threadheads/shared';
import { parseMarkerError, trpcErrorCode, type MarkerErrorInfo } from '@/lib/trpc-error-utils';
import { audienceLabel } from '@/features/base-garments/lib/base-garments-audience';
import { anyBaseGarmentErrorText, parseBaseGarmentError } from '@/features/base-garments/lib/base-garment-errors';

export type SupplierCatalogErrorInfo = MarkerErrorInfo<SupplierCatalogErrorKey>;

/** The catalog refusal behind a failed call, or null when it is not one. */
export const parseSupplierCatalogError = (error: unknown): SupplierCatalogErrorInfo | null =>
  parseMarkerError(error, 'SUPPLIER_CATALOG_', SUPPLIER_CATALOG_ERROR_KEYS);

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** DETAIL `count` when it is a whole number above 0, else null (the sentence then drops the number). */
const countOf = (detail: Record<string, unknown>): number | null => {
  const value = detail.count;
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
};

/**
 * `key` with `{count}` (its `_one` / `_other` forms) when the refusal carries a
 * count, else the count-less `key` itself ("Some of these sizes ...").
 */
function countedText(
  t: TFunction<'inventory'>,
  key: string,
  detail: Record<string, unknown>,
  values: Record<string, string> = {},
): string {
  const count = countOf(detail);
  return count === null ? t(key, values) : t(key, { ...values, count });
}

/** Which part of the Add form a refusal is about, so its sentence sits there. */
export type AddErrorField = 'code' | 'codes' | 'stockType' | 'form';

export interface AddErrorContext {
  productCode: string;
  supplierName: string;
  locationName: string;
  clients: ReadonlyArray<{ id: string; name: string }>;
}

export interface AddErrorResult {
  text: string;
  field: AddErrorField;
  catalog: SupplierCatalogErrorInfo | null;
}

/** The sentence and the field for a failed Add from catalog. */
export function addErrorText(t: TFunction<'inventory'>, error: unknown, ctx: AddErrorContext): AddErrorResult {
  const catalog = parseSupplierCatalogError(error);
  if (catalog) {
    switch (catalog.key) {
      case 'SUPPLIER_NOT_LINKED':
        return { text: t('supplierCatalog.errors.notLinked', { supplier: ctx.supplierName }), field: 'form', catalog };
      case 'CATALOG_VARIANT_MISMATCH':
        return { text: t('supplierCatalog.errors.variantGone'), field: 'form', catalog };
      case 'CODE_AUDIENCE_MISMATCH': {
        const audience = text(catalog.detail.audience);
        const clientId = text(catalog.detail.clientOrganizationId);
        const clientName = ctx.clients.find((client) => client.id === clientId)?.name ?? null;
        const stockType =
          audience === 'client' || audience === 'all_clients' || audience === 'own'
            ? audienceLabel(t, audience, clientName)
            : t('baseGarments.who.onlyUs');
        return {
          text: t('supplierCatalog.errors.codeStockType', {
            code: text(catalog.detail.code) || ctx.productCode,
            stockType,
          }),
          field: 'code',
          catalog,
        };
      }
      case 'NOT_A_BLANK':
        return { text: t('supplierCatalog.errors.notABlank', { code: ctx.productCode }), field: 'code', catalog };
      case 'NOT_LOCATION_OWNER':
        return { text: t('supplierCatalog.errors.notOwner', { location: ctx.locationName }), field: 'form', catalog };
      // The two refusals of a switch to Just in time on a size already at the
      // location (its Supply goes through the same RPC as Base garments).
      case 'PREPRINT_RESERVED':
        return {
          text: countedText(t, 'supplierCatalog.errors.preprintReserved', catalog.detail, { location: ctx.locationName }),
          field: 'form',
          catalog,
        };
      case 'OPEN_TRANSFER':
        return {
          text: countedText(t, 'supplierCatalog.errors.openTransfer', catalog.detail, { location: ctx.locationName }),
          field: 'form',
          catalog,
        };
      case 'INVALID_INPUT':
      default:
        return { text: t('supplierCatalog.errors.invalid'), field: 'form', catalog };
    }
  }
  const baseGarment = parseBaseGarmentError(error);
  const sentence = anyBaseGarmentErrorText(t, error, ctx.productCode);
  if (baseGarment?.key === 'CODE_TAKEN') return { text: sentence, field: 'code', catalog: null };
  if (baseGarment?.key === 'CODE_NAME_TAKEN' || baseGarment?.key === 'CODE_TABLE_FULL') {
    return { text: sentence, field: 'codes', catalog: null };
  }
  if (baseGarment?.key === 'CLIENT_NOT_CONNECTED' || baseGarment?.key === 'CLIENT_CONFLICT') {
    return { text: sentence, field: 'stockType', catalog: null };
  }
  if (!baseGarment && trpcErrorCode(error) === 'FORBIDDEN') {
    return { text: t('supplierCatalog.errors.forbidden'), field: 'form', catalog: null };
  }
  return { text: sentence, field: 'form', catalog: null };
}

/**
 * The sentence for a failed Supply save (`inventory.setSupply`, Base garments).
 * The refusals are the RPC's own (P0985 not the owner, P0984 not a blank,
 * P0989 stock reserved or held at the location, P0991 a transfer still open
 * there; the last two only refuse a switch to Just in time); the handler's
 * owner guard and the permission check are a plain FORBIDDEN.
 */
export function supplyErrorText(t: TFunction<'inventory'>, error: unknown, locationName: string): string {
  const catalog = parseSupplierCatalogError(error);
  if (catalog) {
    switch (catalog.key) {
      case 'NOT_LOCATION_OWNER':
        return t('supplierCatalog.errors.supplyNotOwner', { location: locationName });
      case 'PREPRINT_RESERVED':
        return countedText(t, 'supplierCatalog.errors.supplyReserved', catalog.detail);
      case 'OPEN_TRANSFER':
        return countedText(t, 'supplierCatalog.errors.supplyOpenTransfer', catalog.detail, { location: locationName });
      case 'NOT_A_BLANK':
        return t('supplierCatalog.errors.supplyNotABlank');
      default:
        return t('supplierCatalog.errors.supplyInvalid');
    }
  }
  if (trpcErrorCode(error) === 'FORBIDDEN') return t('supplierCatalog.errors.supplyForbidden', { location: locationName });
  return t('supplierCatalog.errors.generic');
}

/**
 * The sentence for a failed Order and wait Send or Arrived (BLU-1506). The
 * refusals are the RPCs' own: P0992 the PO is not an Order and wait PO of the
 * caller's org (gone, or never was), P0993 it is no longer in the state the
 * button expects (sent already, or not sent yet), P0994 Send with nothing left
 * to send, P0985 the caller does not run the floor. A plain FORBIDDEN is the
 * permission check.
 */
export function orderAndWaitErrorText(t: TFunction<'inventory'>, error: unknown, poNumber: string): string {
  const catalog = parseSupplierCatalogError(error);
  const po = poNumber || t('supplierCatalog.errors.oawThisOrder');
  if (catalog) {
    switch (catalog.key) {
      case 'OAW_PO_NOT_FOUND':
        return t('supplierCatalog.errors.oawNotFound', { po });
      case 'OAW_PO_WRONG_STATUS':
        return t('supplierCatalog.errors.oawWrongStatus', { po });
      case 'OAW_PO_EMPTY':
        return t('supplierCatalog.errors.oawEmpty', { po });
      case 'NOT_LOCATION_OWNER':
        return t('supplierCatalog.errors.oawNotOwner');
      default:
        return t('supplierCatalog.errors.oawInvalid');
    }
  }
  if (trpcErrorCode(error) === 'FORBIDDEN') return t('supplierCatalog.errors.oawForbidden');
  return t('supplierCatalog.errors.generic');
}

/**
 * The sentence for a failed "When to buy" save (`suppliers.setSupply`, admin
 * only) or a failed Follow supplier stock save (`suppliers.setFollowStock`).
 * P0995 the supplier is not the caller's (removed, or another org's);
 * INVALID_INPUT on `sendTime` is a daily send with no time;
 * FOLLOW_NOT_FOR_ORDER_AND_WAIT is Follow turned on for an Order and wait
 * supplier (another tab switched it).
 */
export function supplierSupplyErrorText(t: TFunction<'inventory'>, error: unknown, supplierName: string): string {
  const catalog = parseSupplierCatalogError(error);
  if (catalog?.key === 'SUPPLIER_NOT_FOUND') return t('supplierCatalog.errors.supplyNotFound', { supplier: supplierName });
  if (catalog?.key === 'FOLLOW_NOT_FOR_ORDER_AND_WAIT') return t('supplierCatalog.errors.followNotForOrderAndWait');
  if (catalog?.key === 'INVALID_INPUT') {
    return text(catalog.detail.field) === 'sendTime'
      ? t('supplierCatalog.errors.supplySendTime')
      : t('supplierCatalog.errors.supplyWhenInvalid');
  }
  if (catalog) return t('supplierCatalog.errors.supplyWhenInvalid');
  if (trpcErrorCode(error) === 'FORBIDDEN') return t('supplierCatalog.errors.supplyWhenForbidden');
  return t('supplierCatalog.errors.generic');
}

/**
 * The sentence for a failed Link or Add to my suppliers.
 *
 * Link's two conflicts carry their own key: the catalog supplier is already
 * linked to another of the org's suppliers (`CATALOG_SUPPLIER_ALREADY_LINKED`,
 * naming it when the server could read its title), or the org's supplier is
 * already linked to another catalog supplier (`SUPPLIER_ALREADY_LINKED`).
 * The rest carry no cause code, so the tRPC code picks the words: CONFLICT
 * for a name the org already uses (Add) or a supplier that changed while it
 * was being linked (Link), NOT_FOUND for a catalog supplier that is gone.
 *
 * `orgSupplierTitle` is the org's supplier the operator picked to link, when
 * there is one.
 */
export function linkErrorText(
  t: TFunction<'inventory'>,
  error: unknown,
  action: 'link' | 'add',
  supplierName: string,
  orgSupplierTitle?: string | null,
): string {
  const catalog = parseSupplierCatalogError(error);
  if (catalog?.key === 'CATALOG_SUPPLIER_ALREADY_LINKED') {
    const linked = text(catalog.detail.linkedSupplierTitle).trim();
    return linked
      ? t('supplierCatalog.errors.catalogLinkedTo', { catalog: supplierName, linked })
      : t('supplierCatalog.errors.catalogLinkedElsewhere', { catalog: supplierName });
  }
  if (catalog?.key === 'SUPPLIER_ALREADY_LINKED') {
    return orgSupplierTitle
      ? t('supplierCatalog.errors.supplierLinkedElsewhere', { supplier: orgSupplierTitle })
      : t('supplierCatalog.errors.supplierLinkedElsewhereUnnamed');
  }
  switch (trpcErrorCode(error)) {
    case 'CONFLICT':
      return action === 'add'
        ? t('supplierCatalog.errors.nameTaken', { supplier: supplierName })
        : t('supplierCatalog.errors.supplierChanged');
    case 'NOT_FOUND':
      return t('supplierCatalog.errors.supplierGone', { supplier: supplierName });
    case 'FORBIDDEN':
      return t('supplierCatalog.errors.forbidden');
    case 'BAD_REQUEST':
      return t('supplierCatalog.errors.inactiveSupplier');
    default:
      return t('supplierCatalog.errors.generic');
  }
}
